import { useCallback, useEffect, useState } from "react"
import {
	IconClockTimeOutline18 as History,
	IconComposeOutline18 as NewChat,
} from "nucleo-ui-outline-18"

import { formatRelativeTime } from "@productlane/lib/src/date"
import {
	DropdownMenu,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	IconButton,
	Loader,
	Tooltip,
} from "@productlane/ui"

import type { RecentChatSession } from "./sidebar-ai-chat-api"
import { toast } from "@/components/toast"
import {
	shouldRememberCopilotSession,
	useCopilotState,
} from "@/lib/states/copilot-state"
import { sidebarAiChatApi } from "./sidebar-ai-chat-api"
import { planNewSidebarAiChat } from "./sidebar-ai-chat-new-chat"
import { useSidebarAiChatState } from "./sidebar-ai-chat-state"
import { abortActiveSidebarAiChatStream } from "./sidebar-ai-chat-sync"

const truncatePreview = (preview: string | null) => {
	if (!preview) return "Empty chat"
	const trimmed = preview.trim().replace(/\s+/g, " ")
	if (trimmed.length === 0) return "Empty chat"
	return trimmed.length > 60 ? `${trimmed.slice(0, 60)}…` : trimmed
}

export const SidebarAiChatHeader = () => {
	const sessionId = useSidebarAiChatState((s) => s.sessionId)
	const scope = useSidebarAiChatState((s) => s.scope)
	const threadId = scope?.kind === "thread" ? scope.threadId : null
	const messages = useSidebarAiChatState((s) => s.messages)
	const startNewChat = useSidebarAiChatState((s) => s.startNewChat)
	const switchSession = useSidebarAiChatState((s) => s.switchSession)

	const [recent, setRecent] = useState<RecentChatSession[] | null>(null)
	const [isLoadingRecent, setIsLoadingRecent] = useState(false)
	const [historyOpen, setHistoryOpen] = useState(false)

	const refreshRecent = useCallback(async () => {
		setIsLoadingRecent(true)
		try {
			const sessions = await sidebarAiChatApi.listRecentSessions(threadId)
			setRecent(sessions)
		} catch {
			setRecent([])
		} finally {
			setIsLoadingRecent(false)
		}
	}, [threadId])

	// Refetch the list whenever the dropdown opens, or when the active
	// session changes while the menu is open (so it reflects the freshly
	// archived session immediately).
	useEffect(() => {
		if (historyOpen) {
			void refreshRecent()
		}
	}, [historyOpen, sessionId, refreshRecent])

	// "New chat" is a no-op if the current chat is already empty — we don't
	// want to spam the database with abandoned sessions if the user clicks
	// the button repeatedly without ever sending a message. We also don't
	// pre-allocate a session here; the next user message will lazily create
	// one (see SidebarAiChatSync). Until then, the chat is just an empty
	// in-memory state with no `sessionId`.
	const isCurrentChatEmpty = !sessionId || (messages?.length ?? 0) === 0

	const handleNewChat = useCallback(() => {
		const plan = planNewSidebarAiChat({ scope, isCurrentChatEmpty })
		if (plan.focusScope) {
			useCopilotState.getState().requestFocus(plan.focusScope)
		}
		if (!plan.startsFreshChat) return
		abortActiveSidebarAiChatStream()
		if (plan.clearsRememberedSession && scope) {
			useCopilotState.getState().setRememberedSessionId(scope, null)
		}
		startNewChat()
	}, [isCurrentChatEmpty, scope, startNewChat])

	const handlePickSession = useCallback(
		async (id: string) => {
			if (id === sessionId) {
				setHistoryOpen(false)
				return
			}
			try {
				abortActiveSidebarAiChatStream()
				const messages = await sidebarAiChatApi.loadHistory(id)
				switchSession(id, messages)
				if (scope && shouldRememberCopilotSession(scope)) {
					useCopilotState.getState().setRememberedSessionId(scope, id)
				}
				setHistoryOpen(false)
			} catch {
				toast.error({ title: "Failed to load chat" })
			}
		},
		[sessionId, scope, switchSession],
	)

	return (
		<div className="absolute left-3 top-3.5 z-10 flex w-fit items-center gap-1">
			<Tooltip label="New chat" side="bottom">
				<IconButton
					Icon={NewChat}
					iconStrokeWidth={1.5}
					variant="ghost"
					size="default"
					aria-label="New chat"
					onClick={() => void handleNewChat()}
				/>
			</Tooltip>

			<DropdownMenu
				open={historyOpen}
				onOpenChange={setHistoryOpen}
				trigger={
					<div>
						<Tooltip label="Recent chats" side="bottom">
							<IconButton
								Icon={History}
								iconStrokeWidth={1.5}
								variant="ghost"
								size="default"
								aria-label="Recent chats"
							/>
						</Tooltip>
					</div>
				}
				contentProps={{ align: "start", sideOffset: 6 }}
				className="w-72"
			>
				<DropdownMenuLabel className="select-none text-xs font-medium text-tertiary">
					Recent chats
				</DropdownMenuLabel>
				<DropdownMenuSeparator />

				{isLoadingRecent && recent === null ? (
					<DropdownMenuItem
						disabled
						className="flex w-full select-none justify-center !opacity-100"
					>
						<Loader size={8} className="select-none" />
					</DropdownMenuItem>
				) : !recent || recent.length === 0 ? (
					<div className="px-2 py-2 text-sm text-tertiary">
						No previous chats yet
					</div>
				) : (
					recent.map((s) => {
						const isActive = s.id === sessionId
						return (
							<DropdownMenuItem
								key={s.id}
								onSelect={() => void handlePickSession(s.id)}
								className="flex flex-col items-start gap-0.5"
							>
								<div className="flex w-full items-center justify-between gap-2">
									<span className="truncate text-sm font-medium text-primary">
										{truncatePreview(s.preview)}
									</span>
									{isActive ? (
										<span className="shrink-0 text-[10px] font-medium tracking-wide text-tertiary">
											Active
										</span>
									) : null}
								</div>
								<span className="text-xs text-tertiary">
									{formatRelativeTime(new Date(s.updatedAt))} · {s.messageCount}{" "}
									message{s.messageCount === 1 ? "" : "s"}
								</span>
							</DropdownMenuItem>
						)
					})
				)}
			</DropdownMenu>
		</div>
	)
}
